import { useTranslation } from "react-i18next";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Zap, UserIcon, MapPin, Building2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NODE_HEIGHT } from "./types";

type TriggerUser = {
  profileImage?: string | null;
  firstName?: string;
  lastName?: string;
  employeeId?: string;
  position?: string;
  department?: string;
  branch?: string;
};

type TriggerNodeData = {
  user?: TriggerUser;
  triggerContent?: React.ReactNode;
} & Record<string, unknown>;

const TriggerNode = ({ data }: NodeProps) => {
  const { t } = useTranslation(["common"]);
  const d = data as TriggerNodeData;
  const u = d.user;

  return (
    <div
      className="border border-border rounded-xl bg-white w-[372px] shadow-xs overflow-hidden flex flex-col"
      style={{ height: NODE_HEIGHT }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 shrink-0 bg-info-muted">
        <div className="w-6 h-6 bg-info-muted rounded-xs flex items-center justify-center shrink-0">
          <Zap fill="var(--color-primary)" className="size-3.5 text-info" />
        </div>
        <span className="text-sm font-semibold text-info">
          {t("common:workflow.starting_point")}
        </span>
      </div>

      {/* Requester */}
      <div className="flex items-start gap-2.5 px-4 py-3 shrink-0 border-b border-gray-100">
        <Avatar className="size-9 bg-primary/10 shrink-0">
          <AvatarImage
            src={u?.profileImage ?? ""}
            alt={t("common:workflow.requester")}
          />
          <AvatarFallback>
            <UserIcon size={13} className="text-white" />
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1 pt-0.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-xs font-semibold text-gray-900 truncate">
              {u?.firstName} {u?.lastName}
            </span>
            {u?.employeeId && (
              <span className="text-[10px] text-gray-400 bg-white border border-border px-1.5 py-px rounded-full shrink-0 font-medium leading-none">
                {u.employeeId}
              </span>
            )}
          </div>
          {u?.position && (
            <span className="text-[11px] mt-0.5 block truncate text-gray-500">
              {u.position}
            </span>
          )}
          {u?.department && (
            <div className="flex items-center gap-1 mt-0.5">
              <Building2 className="size-2.5 shrink-0 text-gray-400" />
              <span className="text-[10px] truncate text-gray-400">
                {u.department}
              </span>
            </div>
          )}
          {u?.branch && (
            <div className="flex items-center gap-1 mt-0.5">
              <MapPin className="size-2.5 shrink-0 text-gray-400" />
              <span className="text-[10px] truncate text-gray-400">
                {u.branch}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Trigger content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
        {d.triggerContent}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="bg-white! border-2! border-primary! w-3! h-3!"
        isConnectable={false}
      />
    </div>
  );
};

export default TriggerNode;
